import { useCallback } from 'react';
import { useNavigate, useParams, type NavigateOptions } from 'react-router-dom';
import type { OrgDoc } from '../types';

/**
 * Org-scoped URLs.
 *
 * Every application route lives under `/{org-slug}/…`, so a link someone pastes
 * into a message says which workspace it belongs to and opens there even if the
 * reader was last looking at a different one. The slug is the source of truth
 * for the active org; the org selection follows the URL, never the reverse.
 *
 * An org that predates slugs, or whose slug is somehow blank, falls back to its
 * id — an ugly URL beats an unreachable workspace.
 */

/** The URL segment for an org. */
export function orgSegment(org: Pick<OrgDoc, 'orgId' | 'slug'>): string {
  return org.slug || org.orgId;
}

/** The segment currently in the URL, or null outside the org routes. */
export function useOrgSegment(): string | null {
  return useParams().orgSlug ?? null;
}

/**
 * Prefixes an app path with the current org segment.
 *
 * Paths are written the way they always were — `/projects`, `/tasks/${id}` —
 * and gain the workspace prefix here, so a screen never has to know the slug.
 */
export function useOrgPath(): (path: string) => string {
  const segment = useOrgSegment();
  return useCallback(
    (path: string) => {
      const suffix = path === '/' ? '' : path.startsWith('/') ? path : `/${path}`;
      // Outside the org routes there is nothing to prefix with; returning the
      // bare path keeps sign-in and org creation working.
      return segment ? `/${segment}${suffix}` : suffix || '/';
    },
    [segment],
  );
}

/** `navigate`, with the org prefix applied. */
export function useOrgNavigate(): (path: string, options?: NavigateOptions) => void {
  const navigate = useNavigate();
  const orgPath = useOrgPath();
  return useCallback(
    (path: string, options?: NavigateOptions) => navigate(orgPath(path), options),
    [navigate, orgPath],
  );
}

/**
 * Top-level segments that used to be routes in their own right.
 *
 * A bookmark or an email link to `/reports` predates the slug and would
 * otherwise be read as a workspace named "reports". Recognising them means the
 * old link still lands on the right screen instead of silently on the
 * dashboard.
 */
export const LEGACY_ROOTS = new Set([
  'my-tasks',
  'planner',
  'projects',
  'clients',
  'tasks',
  'time',
  'leave',
  'reports',
  'notifications',
  'settings',
  'team',
  'trash',
  'finance',
  'goals',
  'capacity',
]);
